import React from 'react';
import { Box, IconButton, styled } from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { getChannelsByComposableDevice } from '../../../util/getChannelsByComposableDevice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { Merchandiser } from '../../../redux/composableDevice/model/Merchandiser.model';
import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../../../redux/commander/selectors';
import { useControlUnitsByIds } from '../../../../../redux/controlUnit/hooks/useControlUnitsByIds';
import { getControlUnitsActiveByIdFilter } from '../../../../../redux/controlUnit/selectors';
import { putGroup } from '../../../../../redux/groups/actions/thunks';
import { ExternalLinkIcon, UpdateIcon } from '../../../theme/icons';
import useMerchandiserUIAction from '../MerchandiserSubheader/MerchandiserActions/MerchandiserUIAction';
import TooltipPrimary from '../../../../../theme/components/TooltipPrimary';
import { useShowErrorMessage } from '../../../../../handlingErrors';

interface NoControlUnitsViewProps {
  merchandiser: Merchandiser;
}
export const NoControlUnitsView = ({
  merchandiser,
}: NoControlUnitsViewProps) => {
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();
  const showError = useShowErrorMessage();

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: merchandiser?.machineType.id,
    })
  );
  const devices =
    useAppSelector((state) =>
      getAllAvailableDevicesToAddByGroupIdSelector(state, {
        groupId: merchandiser?.parentGroupId,
      })
    ) || [];
  const selectedDevice = devices.find(
    (device) => device?.uuid === (merchandiser?.devices?.[0] || '')
  );
  const { data: devicesControlUnits } = useControlUnitsByIds({
    deviceIds: merchandiser?.devices,
  }) || { data: [] };

  const deviceInfo = useMerchandiserUIAction({
    merchandiserId: merchandiser?.id,
  });

  const VPNIcon = {
    icon: <ExternalLinkIcon />,
    view: 'commanderVPN',
    ...deviceInfo,
  };
  const deviceControlUnits = (
    devicesControlUnits?.find(
      (device) => device?.deviceId === selectedDevice?.uuid
    )?.controlUnits || []
  ).filter(getControlUnitsActiveByIdFilter);

  const updateChannels = async () => {
    try {
      const channelList =
        getChannelsByComposableDevice(
          machineType!,
          deviceControlUnits,
          selectedDevice?.uuid || ''
        ) || [];
      if (!channelList.length) {
        showError('freshwatch.merchandiser.add.control_unit_error');
        return;
      }
      await dispatch(
        putGroup(merchandiser?.id ?? '', {
          ...merchandiser,
          composableDevice: {
            ...merchandiser?.composableDevice,
            channels: channelList,
          },
        })
      );
    } catch (error) {
      showError('freshwatch.merchandiser.add.control_unit_error');
    }
  };
  return (
    <Box>
      {VPNIcon.showButton && (
        <TooltipPrimary title={VPNIcon.tooltip} placement="bottom">
          <span>
            <StyledIconButton
              sx={{ boxShadow: 2 }}
              key={VPNIcon.view}
              active={false}
              onClick={VPNIcon.onClick}
              color="primary"
              disabled={VPNIcon.disabled}
            >
              {VPNIcon.icon}
            </StyledIconButton>
          </span>
        </TooltipPrimary>
      )}
      <TooltipPrimary
        title={polyglot.t('freshwatch.merchandiser.update_channels')}
        placement="bottom"
      >
        <span>
          <StyledIconButton
            sx={{ boxShadow: 2 }}
            active={false}
            onClick={updateChannels}
            color="primary"
          >
            <UpdateIcon />
          </StyledIconButton>
        </span>
      </TooltipPrimary>
    </Box>
  );
};

const StyledIconButton = styled(IconButton)<{ active: boolean }>(
  ({ theme, active }) => `
      background-color: ${
        active
          ? theme.palette.secondary.main
          : theme.palette.primary.contrastText
      };
      border-radius: 1rem;
      padding: 1rem;
      height: 3.5rem;
      margin: 0.5rem;
      width: 4rem;
      &:hover {
        background-color: ${theme.palette.primary.light};
      }
      `
);
